import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Switch,
  Checkbox,
  HStack,
  Button,
  ModalFooter,
  useToast,
  Text,
} from '@chakra-ui/react';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import context from '../context';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import {
  CREATE_PAGE_GROUP,
  REMOVE_GROUP_ELEMENT,
  REMOVE_PAGE_GROUP,
  UPDATE_PAGE_GROUP,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Textarea from '../../Form/Textarea';
import sanitizeString from '../../../utils/sanitizeString';

interface Props {
  onCreated?: (data: any) => void;
}

interface FormData {
  name: string;
  description?: string;
  cols: number;
  countdown: boolean;
  height?: string;
  padding?: number;
  width?: string;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
}

const formData = yup.object().shape({
  name: yup.string().nullable(),
  description: yup.string().nullable(),
  schedule: yup.boolean(),
  countdown: yup.boolean(),
  height: yup.string().default('120'),
  padding: yup.number().default(0),
  gap: yup
    .number()
    .typeError('El espaciado es obligatorio, si no lo desea, ingrese 0')
    .default(0),
  vertical_padding: yup
    .number()
    .typeError('El padding es obligatorio, si no lo desea, ingrese 0')
    .default(0),
  horizontal_padding: yup
    .number()
    .typeError('El padding es obligatorio, si no lo desea, ingrese 0')
    .default(0),
  rounded_size: yup
    .number()
    .typeError('El redondeo es obligatorio, si no lo desea, ingrese 0')
    .default(0),
  cols: yup
    .number()
    .typeError('Las columnas son obligatorias')
    .required('Las columnas son obligatorias')
    .min(2, 'Las columnas deben ser al menos 2')
    .max(3, 'Las columnas deben ser como máximo 3'),
  schedule_start: yup
    .mixed()
    .when('schedule', {
      is: true,
      then: yup.date().required('La fecha de inicio es obligatoria'),
    })
    .optional(),
  schedule_end: yup
    .mixed()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(
            schedule_start,
            'La fecha final debe ser posterior a la fecha de inicio',
          )
          .required('La fecha final es obligatoria');
      }
      return yup.date().required('La fecha final es obligatoria');
    }),
});

const Form: React.FC<Props> = ({ onCreated }) => {
  const toast = useToast();

  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);
  const { formState, handleSubmit, register, control, setValue } = useForm({
    resolver: yupResolver(formData),
  });

  const [removeGroupElement] = useMutation(REMOVE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const [createPageGroup, { loading: createLoading, error: createError }] =
    useMutation(CREATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [updatePageGroup, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [removePageGroup, { loading: deleteLoading, error: deleteError }] =
    useMutation(REMOVE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);
  const { t } = useTranslation();
  const { errors } = formState;

  useEffect(() => {
    if (createError || updateError || deleteError) {
      toast({
        title: 'Opss!!',
        description:
          createError?.message ||
          updateError?.message ||
          deleteError?.message ||
          t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createError, updateError, deleteError]);

  useEffect(() => {
    if (!pageGroup) {
      setValue('vertical_padding', 0);
      setValue('horizontal_padding', 0);
      setValue('rounded_size', 0);
      setValue('cols', 2);
      setValue('height', '120');
      setValue('gap', 2);
      setValue('padding', 0);
      return;
    }
    if (pageGroup?.schedule_end) {
      setValue('schedule', true);
      setValue('schedule_start', new Date(pageGroup.schedule_start));
      setValue('schedule_end', new Date(pageGroup.schedule_end));
      setValue('countdown', pageGroup.countdown);
    }
    setValue('cols', pageGroup?.cols);
    setValue('name', pageGroup.name);
    setValue('description', pageGroup?.description);
    setValue('vertical_padding', pageGroup?.vertical_padding);
    setValue('horizontal_padding', pageGroup?.horizontal_padding);
    setValue('rounded_size', pageGroup?.rounded_size);
    setValue('height', pageGroup?.height);
    setValue('gap', pageGroup?.gap);
    setValue('padding', pageGroup?.padding);
  }, [pageGroup, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);

      const { schedule: scheduleData, ...rest } = sanitizedObj;

      if (pageGroup) {
        if (pageGroup?.cols > data?.cols) {
          const confirmed = window.confirm(t('confirmColumnsDelete'));
          if (confirmed) {
            const index = data?.cols;

            try {
              await removeGroupElement({
                variables: {
                  id: pageGroup?.items[index]?.id,
                },
              });
            } catch (err) {
              //
            }
          }
        }
        await updatePageGroup({
          variables: {
            updatePageGroupInput: {
              ...rest,
              schedule_end: scheduleData ? rest.schedule_end : null,
              padding: Number(data?.padding),
              countdown: scheduleData ? rest.countdown : false,
              id: pageGroup?.id,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        toast({
          title: t('success'),
          description: t('groupEditedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      } else {
        const apiData = await createPageGroup({
          variables: {
            createPageGroupInput: {
              ...rest,
              ...newPageGroupDTO,
              padding: Number(data?.padding),
              element_display_id: display?.id,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        if (onCreated) onCreated(apiData);
        if (onPageGroupCreated) onPageGroupCreated(apiData);
        toast({
          title: t('success'),
          description: t('groupCreatedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // nada
    }
  };

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(t('confirmDeleteGroup'));
      if (confirmed) {
        await removePageGroup({
          variables: {
            id: pageGroup?.id,
          },
        });
        toast({
          title: t('success'),
          description: t('groupDeletedSuccess'),
          status: 'success',
          position: 'top-right',
        });
        if (onClose) onClose();
      }
    } catch {
      // error
    }
  };

  return (
    <>
      <VStack
        as="form"
        spacing="8"
        onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
      >
        <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
          <Input
            label={t('title')}
            error={errors?.name as FieldError | undefined}
            {...register('name')}
          />
          <Textarea
            label={t('subTitle')}
            rows={1}
            error={errors?.description as FieldError | undefined}
            {...register('description')}
          />
          <Input
            label={`${t('columns')} (max 3)`}
            type="number"
            isRequired
            max={3}
            error={errors?.cols as FieldError | undefined}
            {...register('cols')}
          />
        </SimpleGrid>
        <Text
          as="h2"
          fontSize="md"
          fontWeight="bold"
          textAlign="left"
          alignSelf="start"
        >
          Personalizar
        </Text>
        <SimpleGrid minChildWidth="100px" spacing="8" w="100%">
          <Input
            label={t('heightPx')}
            isRequired
            placeholder="Ex: 150px"
            type="number"
            error={errors?.height as FieldError | undefined}
            {...register('height')}
          />
          <Input
            label={t('spacingBetweenBanners')}
            isRequired
            placeholder="2"
            type="number"
            error={errors?.gap as FieldError | undefined}
            {...register('gap')}
          />
        </SimpleGrid>
        <SimpleGrid minChildWidth="100px" spacing="8" w="100%">
          <Input
            label={t('paddingVerticalPx')}
            type="number"
            isRequired
            error={errors?.vertical_padding as FieldError | undefined}
            {...register('vertical_padding')}
          />
          <Input
            label={t('paddingHorizontalPx')}
            type="number"
            isRequired
            error={errors?.horizontal_padding as FieldError | undefined}
            {...register('horizontal_padding')}
          />
          <Input
            label={t('borderRadiusPx')}
            type="number"
            isRequired
            error={errors?.rounded_size as FieldError | undefined}
            {...register('rounded_size')}
          />
        </SimpleGrid>
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="schedule" mb="0">
            {t('schedule')}
          </FormLabel>
          <Switch
            id="schedule"
            size="lg"
            colorScheme="brand"
            isChecked={schedule}
            {...register('schedule')}
            onChange={() => setSchedule(state => !state)}
          />
        </FormControl>
        {schedule && (
          <>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_start')}
                label={t('scheduleStart')}
                showTimeSelect
                error={errors?.schedule_start as FieldError | undefined}
              />
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_end')}
                label={t('scheduleEnd')}
                showTimeSelect
                error={errors?.schedule_end as FieldError | undefined}
              />
            </SimpleGrid>
            <Checkbox
              alignSelf="flex-start"
              colorScheme="brand"
              {...register('countdown')}
            >
              {t('countdown')}
            </Checkbox>
          </>
        )}
      </VStack>

      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={deleteLoading}
            >
              {t('remove')}
            </Button>
          )}
          <Button onClick={onClose}>{t('close')}</Button>
          <Button
            colorScheme="brand"
            isLoading={createLoading || updateLoading}
            onClick={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
          >
            {t('save')}
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

export default Form;
